import type { AppState } from './default-state';
import { effectiveWorldPosition } from './phase';
import { getBody } from '../core/reference-data';
import { meanMotion } from '../core/kepler';
import { segmentsInContact } from '../core/collision';
import { SEMI_MAJOR_AXIS_MIN_METRES } from '../core/ranges';
import type { BodyId, BodyStatus } from '../core/types';

/**
 * Pure collision RESOLUTION (AD-1/AD-2). A `state -> state` transform run INSIDE the
 * clock-advance path (`advanceTime`), so a removal is a consequence of the clock
 * advancing, never a user command: no new verb is added to the closed command union.
 * Pure: no `Date`, no DOM, no wall-clock read; deterministic given its inputs.
 *
 * Two passes over the SAME sub-sampled focus-centred SI positions, decided in SI
 * metres via each body's fixed `collisionRadius` ONLY (FR-13 / AD-10): never sprites,
 * `physicalRadius`, orbit-line geometry, projected pixels, or `scaleMode`.
 *
 *   1. Body-body collision (Story 4.1): every unordered pair of ACTIVE, non-central
 *      orbiters that comes into contact flips BOTH to `status: 'collided'`.
 *   2. Central absorption (Story 4.2 + 4.3): every ACTIVE, non-central orbiter whose
 *      swept path comes within the central body's EFFECTIVE absorption radius (a
 *      bounded floor, `effectiveCentralAbsorptionRadius`) plus the orbiter's radius of
 *      the focus (origin `(0,0)`) flips ONLY that orbiter to `status: 'absorbed'`.
 *      The central body is one party but is never flagged, and its `mu` (reference
 *      data) and every survivor's `a`/`e` are untouched, so absorption is mass-neutral
 *      by construction (Story 4.2 AC 3). Absorption takes precedence when a body would
 *      both collide and be absorbed on one tick: reaching the centre is the outcome.
 */

/**
 * Per-sub-step cap on a body's mean-anomaly advance (radians), ~2 degrees. The
 * interval is sub-sampled so no sub-segment spans more than this, keeping the linear
 * (swept) approximation of each orbital arc accurate enough that a fast body cannot
 * tunnel through another between evaluated frames (AC 1, anti-tunnelling).
 *
 * This is the EXACT accuracy guarantee, honoured for any interval: a tight ceiling on
 * the sub-step count is deliberately rejected, because it would silently degrade the
 * bound once a body advanced more than ceiling * MAX_SUBSTEP_RADIANS over one clock
 * step (a small-`a` orbit at high playback speed), aliasing the swept test so genuine
 * collisions are missed and a single large `dt` disagrees with many small ones. The
 * sub-step count is derived purely from the fastest body's mean-anomaly advance over
 * the interval; the interval itself is physically bounded by the caller (the animation
 * loop clamps each real frame to <=1/15 s), so for every legitimate orbit the count
 * stays around 12k or below (~12k at a = 0.01 AU, max speed, one 1/15 s frame). A
 * far-above-legitimate MAX_SUBSTEP_COUNT is applied to
 * the count purely as a crash backstop (see its docblock); it sits so far above the
 * legitimate maximum that it never engages for legitimate input and so does NOT weaken
 * this accuracy bound.
 */
export const MAX_SUBSTEP_RADIANS = Math.PI / 90;

/**
 * A crash backstop on the sub-step count, NOT the accuracy bound. The accuracy bound is
 * MAX_SUBSTEP_RADIANS above; this ceiling exists only so a residual large-but-finite `n`
 * that reaches this loop by some future route degrades gracefully instead of allocating
 * an unbounded positions array and hanging or OOM-crashing the tab.
 *
 * It is set far above every legitimate `n`. Note the naive "at most ~24 simulated days,
 * so ~12k sub-steps" reasoning does NOT hold in general: with a planet at the centre the
 * playback `selectSpeedScale = sqrt(muSun / muCentral)` inflates the simulated `dt`. What
 * IS bounded is the mean-anomaly ADVANCE that drives `n`: for the fastest orbiter,
 * `n_orbiter * dt = sqrt(muCentral / a^3) * realFrame * speedDaysPerSecond *
 * sqrt(muSun / muCentral) * SECONDS_PER_DAY`, in which muCentral cancels, so the advance
 * depends only on `muSun`, the orbiter's `a`, the clamped real frame, and the speed
 * cap, independent of which body is the centre. At the editable minimum a = 0.01 AU that
 * advance is ~418 radians, i.e. `n` ~= 12k, about 17x under this 200k cap.
 *
 * If the cap ever engaged it would alias ONLY the Pass-1 body-body swept test (a fast
 * body could tunnel past another between coarsened sub-steps); Pass-2 central absorption
 * is analytic and exact for ANY `n`, so a capped `n` never makes the centre miss or
 * falsely absorb a body. The PRIMARY defence against the underflow-`a` class is the O(1)
 * skip in the orbiter-collection loop (in resolveCollisions below), not this cap.
 */
export const MAX_SUBSTEP_COUNT = 200_000;

/** One full turn in radians; a periapsis passage is a step in `floor(phase / 2π)`. */
const TWO_PI = 2 * Math.PI;

/**
 * Minimum body-body EFFECTIVE collision radius, in SI world metres (Story 4.3,
 * FR-13 / AD-2 / AD-10). The true reference-data `collisionRadius` values (Mercury
 * 2,440 km .. Jupiter 70,000 km) are thousands of times smaller than the on-screen
 * marker and ~86x smaller than two bodies' lockstep separation, so a learner who
 * deliberately overlaps two orbits sees the markers touch yet no collision fires.
 * This floor inflates the DETECTION cross-section in WORLD metres so "overlap the
 * orbits -> they collide" works, while staying deterministic and INDEPENDENT of
 * zoom, `scaleMode`, projected pixels, and the share code (it is an SI constant in
 * `state`, never read from `render`).
 *
 * A FIXED FLOOR is chosen over a pure multiplier: a multiplier (~90x) would give
 * every body a different forgiving zone (Jupiter ~6e9 m, Mercury ~2e8 m), so pairs
 * of small bodies stay under-forgiving while Jupiter pairs over-forgive; a floor
 * gives every pair the SAME world-space cross-section, so "overlap the orbits"
 * behaves uniformly across the Mercury..Jupiter size range.
 *
 * Sizing (from the ticket's probe): a floored pair sums to 2 * 7.5e8 = 1.5e9 m
 * (~1.5 million km). That EXCEEDS the reporter's lockstep Venus/Earth separation
 * (~1.07e9 m, when Earth's `a` is set to Venus's), so the exact lockstep case now
 * collides; yet it sits ~25x below the default system's tightest planet-pair gap
 * (~3.76e10 m), and ~2.8x below the tightest APPROACH the existing suite relies on
 * staying apart, so the default nine-body system never self-collides. This body-body
 * floor is applied ONLY to the Pass-1 test; Pass-2 central absorption uses its OWN,
 * separate bounded floor (`MIN_ABSORPTION_RADIUS_M`, much larger) so the two concerns
 * are tuned independently and neither can make the Sun swallow Mercury (periapsis
 * ~0.307 AU) on load (Story 4.3 AC-4).
 */
export const MIN_COLLISION_RADIUS_M = 7.5e8;

/**
 * The world-space EFFECTIVE collision radius for a body, DERIVED from its true
 * `collisionRadius` without mutating reference data: `max(trueRadius,
 * MIN_COLLISION_RADIUS_M)`. Used for the body-body Pass-1 detection distance only.
 */
export function effectiveCollisionRadius(trueRadius: number): number {
  return trueRadius > MIN_COLLISION_RADIUS_M ? trueRadius : MIN_COLLISION_RADIUS_M;
}

/**
 * Minimum EFFECTIVE central-absorption radius, in SI world metres (Story 4.3, revised
 * per a user report). Story 4.3 originally left Pass 2 (central absorption) on the TRUE
 * central radius (its option (a)); a deliberate high-eccentricity dive then never
 * reached the Sun's tiny true radius and kept orbiting, contrary to the learner's
 * intent (Sun at centre, Earth a = 1 AU, e = 0.95 -> periapsis 0.05 AU = 7.48e9 m, far
 * outside the Sun's true 6.957e8 m radius). This bounded floor (Story 4.3 option (b))
 * inflates ONLY the central-absorption cross-section so a close plunge is absorbed,
 * while staying FAR below Mercury's default periapsis (~0.307 AU = 4.6e10 m) so the
 * default nine-body system is NEVER absorbed (Story 4.3 AC-4).
 *
 * At 1.0e10 m (~0.0668 AU) it absorbs any orbiter whose periapsis reaches <= ~0.067 AU
 * (Earth at e >= ~0.933, so the eccentricity slider's max of 0.95 is caught with
 * margin) yet sits ~4.6x below Mercury's periapsis. Like MIN_COLLISION_RADIUS_M it is
 * an SI constant in `state`: deterministic and independent of zoom, scaleMode, pixels,
 * and the share code. It is applied ONLY to the Pass-2 central body; the orbiter keeps
 * its true radius (its contribution is negligible against this floor).
 */
export const MIN_ABSORPTION_RADIUS_M = 1.0e10;

/**
 * The world-space EFFECTIVE central-absorption radius, DERIVED from the central body's
 * true `collisionRadius` without mutating reference data: `max(trueRadius,
 * MIN_ABSORPTION_RADIUS_M)`. Used for the Pass-2 central-absorption distance only.
 */
export function effectiveCentralAbsorptionRadius(trueRadius: number): number {
  return trueRadius > MIN_ABSORPTION_RADIUS_M ? trueRadius : MIN_ABSORPTION_RADIUS_M;
}

interface ActiveOrbiter {
  readonly id: BodyId;
  readonly a: number;
  readonly e: number;
  readonly offset: number;
  readonly collisionRadius: number;
}

export function resolveCollisions(
  state: AppState,
  prevTime: number,
  newTime: number
): AppState {
  const muCentral = getBody(state.centralBody).mu;

  // The ACTIVE, non-central orbiters. A body already `collided`/`absorbed` is
  // excluded, so it is never re-detected: running twice yields the same result and a
  // repeated tick over already-removed bodies produces no further change (AC 9).
  const orbiters: ActiveOrbiter[] = [];
  for (const b of state.bodies) {
    if (b.id === state.centralBody || b.status !== 'active') continue;
    if (b.a <= 0) continue; // a non-orbiting element has no swept path
    // PRIMARY O(1) guard (SEC-1). Skip any orbiter whose `a` is below the editable
    // minimum, or whose `a` is NaN. (The `!Number.isFinite(meanMotion)` clause is the NaN
    // backstop: a NaN `a` slips both `<= 0` and `< MIN`, so only this catches it; an
    // underflow `a` is already caught by `< MIN`.) A sub-min `a` cannot arise from a UI
    // edit or a share link: the editable range is enforced at the UI (reject) and codec
    // (clamp) boundaries, and SwitchCentre only ever transfers in-range values, so this
    // guard defends a future or raw-command route. Why it matters: an underflow `a`
    // (<= ~1.7e-108, e.g. 1e-300) makes meanMotion = sqrt(mu / a^3) = Infinity, so at
    // prevTime > 0 every sub-sampled position is NaN and the body silently defeats BOTH
    // passes (every `NaN <= r` is false, so it is never removed) AND explodes the
    // sub-step count. Skipping neutralises the whole class at O(1) rather than paying the
    // secondary cap's cost.
    if (b.a < SEMI_MAJOR_AXIS_MIN_METRES || !Number.isFinite(meanMotion(b.a, muCentral))) {
      continue;
    }
    orbiters.push({
      id: b.id,
      a: b.a,
      e: b.e,
      offset: state.phaseOffsets[b.id] ?? 0,
      collisionRadius: getBody(b.id).collisionRadius
    });
  }
  // A single active orbiter cannot collide with another body but CAN be absorbed by
  // the centre (Story 4.2), so the pass runs for one or more orbiters; only the
  // body-body pairing below needs a second orbiter.
  if (orbiters.length === 0) return state;

  // Sub-sample count from the LARGEST per-body mean-anomaly advance over the interval,
  // so the fastest body's arc is resolved finely: every sub-segment advances the fastest
  // body by <= MAX_SUBSTEP_RADIANS, keeping the swept test accurate for any interval.
  // The count is bounded in practice because the caller bounds the interval (see
  // MAX_SUBSTEP_RADIANS); it is additionally clamped to MAX_SUBSTEP_COUNT purely as a
  // crash backstop set far above every legitimate `n`, so the clamp never engages for
  // legitimate input and does not weaken the accuracy bound.
  const dt = newTime - prevTime;
  let maxDeltaMeanAnomaly = 0;
  for (const o of orbiters) {
    const delta = Math.abs(meanMotion(o.a, muCentral) * dt);
    if (delta > maxDeltaMeanAnomaly) maxDeltaMeanAnomaly = delta;
  }
  const n = Math.min(
    MAX_SUBSTEP_COUNT,
    Math.max(1, Math.ceil(maxDeltaMeanAnomaly / MAX_SUBSTEP_RADIANS))
  );

  // Positions of every orbiter at each of the n+1 sub-step boundaries (computed once).
  const positions: Array<Array<{ x: number; y: number }>> = orbiters.map((o) => {
    const path: Array<{ x: number; y: number }> = [];
    for (let k = 0; k <= n; k++) {
      const t = prevTime + (dt * k) / n;
      path.push(effectiveWorldPosition({ a: o.a, e: o.e }, muCentral, t, o.offset));
    }
    return path;
  });

  // Per-body outcome for this tick. Body-body contact marks both members
  // `'collided'`; central absorption then overwrites the orbiter with `'absorbed'`
  // (absorption wins if a body would qualify for both on one tick).
  const removed = new Map<BodyId, BodyStatus>();

  // Pass 1 (Story 4.1) — every unordered active pair: contact on ANY sub-segment
  // flags both members. Skipped when there is a single orbiter (no pair to test).
  for (let i = 0; i < orbiters.length; i++) {
    for (let j = i + 1; j < orbiters.length; j++) {
      // Story 4.3: the body-body contact distance uses the INFLATED, world-space
      // effective radius (a floor), so deliberately overlapped orbits collide. This
      // is applied HERE ONLY; the struct keeps the true `collisionRadius`, and Pass 2
      // below reads that true value, so central absorption is never inflated (AC-4).
      const sumRadii =
        effectiveCollisionRadius(orbiters[i].collisionRadius) +
        effectiveCollisionRadius(orbiters[j].collisionRadius);
      const pi = positions[i];
      const pj = positions[j];
      let hit = false;
      for (let k = 0; k < n; k++) {
        if (segmentsInContact(pi[k], pi[k + 1], pj[k], pj[k + 1], sumRadii)) {
          hit = true;
          break;
        }
      }
      if (hit) {
        removed.set(orbiters[i].id, 'collided');
        removed.set(orbiters[j].id, 'collided');
      }
    }
  }

  // Pass 2 (Story 4.2) — central absorption. Distance to the FIXED focus is analytic,
  // so — unlike the body-body pass — we do NOT reuse the linear-chord swept primitive
  // here. A straight chord between two sub-step points on a convex orbit ALWAYS passes
  // closer to the interior focus than the true arc, UNDER-estimating the focal
  // distance; against a fixed interior focus that chord can dip below the threshold
  // even for an orbit whose true periapsis never reaches the centre (a false-positive
  // that would permanently remove a surviving body, Finding 1). Instead we compute the
  // EXACT minimum focal distance over each sub-interval.
  //
  // For an orbiter the focal radius is r(M) = a*(1 - e*cos E(M)); over one orbit its
  // ONLY minimum is the periapsis a*(1-e) at M ≡ 0 (mod 2π) and its only maximum is the
  // apoapsis at M = π. So over a sub-interval that does NOT straddle a periapsis
  // passage the minimum is at an endpoint (`min(r(M1), r(M2))`); over a sub-interval
  // that DOES straddle M ≡ 0 the true minimum is the periapsis a*(1-e). The endpoint
  // radii are exact from the already-computed positions (`hypot(pos) === a(1-e cosE)`),
  // and the mean anomaly advances monotonically, so a periapsis passage in a sub-step
  // (which spans < MAX_SUBSTEP_RADIANS < 2π) is a change in `floor(phase / 2π)`. This
  // is exact (no chord under-estimate, no grid aliasing) and keeps anti-tunnelling: a
  // fast dive to periapsis is caught by the sub-interval straddling M ≡ 0. Flags ONLY
  // the orbiter, never the centre.
  //
  // Story 4.3 (revised): the central body uses a BOUNDED effective absorption radius
  // (MIN_ABSORPTION_RADIUS_M floor) so a deliberate close plunge — a high-eccentricity
  // dive OR a tiny orbit near the centre — is absorbed, while staying far below
  // Mercury's default periapsis so the default system is never absorbed (AC-4). The
  // SAME effective `sumRadii` feeds BOTH the fast reject and the per-interval minimum
  // below, so they can never disagree.
  const centralRadius = effectiveCentralAbsorptionRadius(getBody(state.centralBody).collisionRadius);
  for (let i = 0; i < orbiters.length; i++) {
    const o = orbiters[i];
    const sumRadii = centralRadius + o.collisionRadius;
    const periapsis = o.a * (1 - o.e);
    // Fast reject: if even the deepest point of the orbit (periapsis) stays outside the
    // summed radii the orbit never reaches the centre. (Correctness does not depend on
    // this — the analytic per-interval minimum can never dip below periapsis — but it
    // skips the sub-interval walk for the common non-absorbing case.)
    if (periapsis > sumRadii) continue;

    const pi = positions[i];
    const nOrbiter = meanMotion(o.a, muCentral);
    let absorbed = false;
    for (let k = 0; k < n; k++) {
      // Exact endpoint focal radii (|position| === a(1 - e cos E)).
      const r0 = Math.hypot(pi[k].x, pi[k].y);
      const r1 = Math.hypot(pi[k + 1].x, pi[k + 1].y);
      let minFocal = r0 < r1 ? r0 : r1;
      // Does this sub-interval straddle a periapsis passage (M ≡ 0 mod 2π)? The
      // continuous phase advances monotonically, so a crossing is a step in
      // `floor(phase / 2π)`; when it happens the true sub-interval minimum is the
      // periapsis, below both endpoints.
      const t0 = prevTime + (dt * k) / n;
      const t1 = prevTime + (dt * (k + 1)) / n;
      const phase0 = nOrbiter * t0 + o.offset;
      const phase1 = nOrbiter * t1 + o.offset;
      if (Math.floor(phase0 / TWO_PI) !== Math.floor(phase1 / TWO_PI)) {
        minFocal = periapsis;
      }
      if (minFocal <= sumRadii) {
        absorbed = true;
        break;
      }
    }
    if (absorbed) removed.set(o.id, 'absorbed');
  }

  // No removal: return the SAME reference (mirrors the reducer no-op convention;
  // avoids render churn). Otherwise flip only the flagged bodies' status, leaving
  // a, e, centralBody, selectedBody, time, speed, legendOrder, phaseOffsets, sectors,
  // camera, and scale ALL untouched (AC 4 / mass-neutral AC 3).
  if (removed.size === 0) return state;
  return {
    ...state,
    bodies: state.bodies.map((b) => {
      const status = removed.get(b.id);
      return status ? { ...b, status } : b;
    })
  };
}
